/** Keep in sync with `src/api/quality/quality-rma-helpers.ts`. */

export type RmaQuantityModelLine = {
  quantity: string;
  models: string;
  reason: string;
};

const REASON_MARK = " | ";

function detachReason(text: string): { body: string; reason: string } {
  if (text.startsWith("| ")) {
    return { body: "", reason: text.slice(2).trim() };
  }
  const index = text.indexOf(REASON_MARK);
  if (index < 0) return { body: text, reason: "" };
  return { body: text.slice(0, index).trim(), reason: text.slice(index + REASON_MARK.length).trim() };
}

function attachReason(body: string, reason: string): string {
  const note = reason.trim();
  if (note === "") return body;
  if (body.trim() === "") return `| ${note}`;
  return `${body}${REASON_MARK}${note}`;
}

export function splitRmaQuantityAndModels(raw: string | null | undefined): RmaQuantityModelLine {
  const text = (raw ?? "").trim();
  if (text === "") {
    return { quantity: "", models: "", reason: "" };
  }
  const { body, reason } = detachReason(text);
  const match = body.match(/^(\d+)\s*[x×]\s*(.+)$/i);
  if (match) {
    return { quantity: match[1], models: match[2].trim(), reason };
  }
  return { quantity: "", models: body.trim(), reason };
}

export function joinRmaQuantityAndModels(quantity: string, models: string): string | null {
  const qty = quantity.trim();
  const model = models.trim();
  if (qty === "" && model === "") {
    return null;
  }
  if (qty !== "" && model !== "") {
    return `${qty}x ${model}`;
  }
  if (qty !== "") {
    return qty;
  }
  return model;
}

export function parseRmaQuantityModelLines(raw: string | null | undefined): RmaQuantityModelLine[] {
  const text = (raw ?? "").replace(/\r\n/g, "\n").trim();
  if (text === "") {
    return [];
  }
  return text
    .split("\n")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((part) => splitRmaQuantityAndModels(part));
}

export function formatRmaQuantityModelLines(lines: readonly RmaQuantityModelLine[]): string | null {
  const parts: string[] = [];
  for (const line of lines) {
    const reason = line.reason?.trim() ?? "";
    if (line.models.trim() === "" && reason === "") {
      continue;
    }
    const joined = joinRmaQuantityAndModels(line.quantity, line.models);
    const withReason = attachReason(joined ?? "", reason);
    if (withReason.trim() !== "") {
      parts.push(withReason);
    }
  }
  return parts.length === 0 ? null : parts.join("\n");
}

export function displayRmaQuantityModelLines(raw: string | null | undefined): RmaQuantityModelLine[] {
  const parsed = parseRmaQuantityModelLines(raw);
  return parsed.length > 0 ? parsed : [{ quantity: "", models: "", reason: "" }];
}
